/**
 * Stellex Tycoon Mini App — root component.
 *
 * Auth flow: Telegram initData → /auth/telegram → JWT in store → render scenes.
 * Four primary scenes: Tycoon (build), Wheel (RNG), Quests (daily), Referrals (invite).
 *
 * Privacy gate: between auth and scenes, players who haven't picked a
 * displayName are blocked behind <DisplayNameGate>. Telegram username is
 * never shown publicly — leaderboards / referrals use displayName only.
 */
import { useState } from "react";
import { useTelegramAuth } from "./hooks/useTelegramAuth";
import { useAuthStore } from "./store/auth.store";
import { TycoonScene } from "./scenes/TycoonScene";
import { QuestsPanel } from "./scenes/QuestsPanel";
import { ReferralsPanel } from "./scenes/ReferralsPanel";
import { WheelScene } from "./scenes/WheelScene";
import { PrizesPanel } from "./scenes/PrizesPanel";
import { LeaderboardScene } from "./scenes/LeaderboardScene";
import { setDisplayName } from "./api/auth.api";

type Tab = "tycoon" | "wheel" | "prizes" | "quests" | "leaderboard" | "referrals";

export function App() {
  const auth = useTelegramAuth();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const [tab, setTab] = useState<Tab>("tycoon");
  const [refreshKey, setRefreshKey] = useState(0);

  const handleReward = () => setRefreshKey((k) => k + 1);

  const needsDisplayName =
    auth.status === "authenticated" && user !== null && !user.displayName;

  return (
    <div className="app">
      <header>
        <div>
          <h1>Stellex Tycoon</h1>
          {user && user.displayName && (
            <p className="tagline">
              @{user.displayName}
              {user.kycTier > 0 && <span className="kyc-badge">KYC {user.kycTier}</span>}
            </p>
          )}
        </div>
      </header>

      {auth.status === "loading" && <div className="loading">Connecting…</div>}

      {auth.status === "no-telegram" && (
        <div className="hint-card">
          <h2>{auth.canDevLogin ? "Local Dev Mode" : "Open via Telegram"}</h2>
          <p className="muted">{auth.reason}</p>
          {auth.canDevLogin ? (
            <DevLoginForm onSubmit={(name) => auth.devLogin?.(name)} />
          ) : (
            <p className="muted">
              This Mini App needs the Telegram client to work. Find <code>@stellex_tycoon_bot</code>{" "}
              and tap the play button.
            </p>
          )}
        </div>
      )}

      {auth.status === "error" && (
        <div className="error-card">
          <h2>Auth failed</h2>
          <p>{auth.message}</p>
        </div>
      )}

      {needsDisplayName && (
        <DisplayNameGate
          onSubmit={async (name) => {
            const updated = await setDisplayName(name);
            setUser(updated);
          }}
        />
      )}

      {auth.status === "authenticated" && !needsDisplayName && (
        <>
          <main key={refreshKey}>
            {tab === "tycoon" && <TycoonScene />}
            {tab === "wheel" && <WheelScene onReward={handleReward} />}
            {tab === "prizes" && <PrizesPanel />}
            {tab === "quests" && <QuestsPanel onReward={handleReward} />}
            {tab === "leaderboard" && <LeaderboardScene />}
            {tab === "referrals" && <ReferralsPanel />}
          </main>

          <nav className="bottom-nav">
            <button
              className={tab === "tycoon" ? "active" : ""}
              onClick={() => setTab("tycoon")}
              type="button"
            >
              <span>🏛️</span>
              <span>Exchange</span>
            </button>
            <button
              className={tab === "wheel" ? "active" : ""}
              onClick={() => setTab("wheel")}
              type="button"
            >
              <span>🎰</span>
              <span>Wheel</span>
            </button>
            <button
              className={tab === "quests" ? "active" : ""}
              onClick={() => setTab("quests")}
              type="button"
            >
              <span>🎯</span>
              <span>Quests</span>
            </button>
            <button
              className={tab === "leaderboard" ? "active" : ""}
              onClick={() => setTab("leaderboard")}
              type="button"
            >
              <span>🏆</span>
              <span>Top</span>
            </button>
            <button
              className={tab === "prizes" ? "active" : ""}
              onClick={() => setTab("prizes")}
              type="button"
            >
              <span>🎁</span>
              <span>Prizes</span>
            </button>
            <button
              className={tab === "referrals" ? "active" : ""}
              onClick={() => setTab("referrals")}
              type="button"
            >
              <span>🤝</span>
              <span>Friends</span>
            </button>
          </nav>
        </>
      )}
    </div>
  );
}

// ─── Display-name gate (blocks all play until a public handle is picked) ─

const DISPLAY_NAME_RE = /^[A-Za-z0-9_]{3,20}$/;

function DisplayNameGate({
  onSubmit,
}: {
  onSubmit: (name: string) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const trimmed = name.trim();
  const formatOk = DISPLAY_NAME_RE.test(trimmed);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formatOk || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(trimmed);
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (err as Error).message ??
        "Could not set display name.";
      setError(typeof msg === "string" ? msg : "That name didn't work — try another.");
      setSubmitting(false);
    }
  };

  return (
    <div className="display-name-gate">
      <div className="display-name-card">
        <h2>Pick your player name</h2>
        <p className="muted">
          This is what other players see on the leaderboard, in referrals, and around the game.
          Your Telegram handle stays private.
        </p>

        <form onSubmit={handleSubmit} className="display-name-form">
          <div className="display-name-input-row">
            <span className="display-name-prefix">@</span>
            <input
              autoFocus
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError(null);
              }}
              placeholder="your_name"
              maxLength={20}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              disabled={submitting}
            />
          </div>

          <div className="display-name-rules">
            <span className={trimmed.length >= 3 && trimmed.length <= 20 ? "ok" : ""}>
              3–20 characters
            </span>
            <span className={trimmed.length > 0 && /^[A-Za-z0-9_]+$/.test(trimmed) ? "ok" : ""}>
              letters, digits, underscore
            </span>
          </div>

          {error && <div className="display-name-error">{error}</div>}

          <button
            type="submit"
            className="display-name-submit"
            disabled={!formatOk || submitting}
          >
            {submitting ? "Saving…" : "Continue"}
          </button>
        </form>

        <p className="display-name-warning muted">
          You'll be able to change this later, but pick something you're happy with —
          frequent renames may be rate-limited.
        </p>
      </div>
    </div>
  );
}

function DevLoginForm({ onSubmit }: { onSubmit: (username: string) => void }) {
  const [name, setName] = useState("");
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
      <input
        placeholder="Pick any username (e.g., alice)"
        value={name}
        onChange={(e) => setName(e.target.value)}
        style={{
          padding: "10px 12px",
          background: "#0a0a0a",
          border: "1px solid #2b3139",
          borderRadius: 8,
          color: "#eaecef",
          fontSize: 14,
        }}
      />
      <button
        onClick={() => name.trim() && onSubmit(name.trim())}
        disabled={name.trim().length < 2}
        style={{
          padding: "10px",
          background: "#f0b90b",
          color: "#000",
          border: "none",
          borderRadius: 8,
          fontWeight: 600,
        }}
        type="button"
      >
        Enter dev mode
      </button>
      <p style={{ fontSize: 11, color: "#848e9c", margin: 0 }}>
        Any username works. Use different names to simulate multiple players.
      </p>
    </div>
  );
}
